import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
  Alert,
  Paper,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  PublicRounded,
  LocalOfferRounded,
  PersonRounded,
  SchoolRounded,
  MenuBookRounded,
  LibraryBooksRounded,
  TuneRounded,
  PeopleAltRounded,
  GroupsRounded,
  EventAvailableRounded,
} from '@mui/icons-material';
import {
  collection,
  documentId,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  where,
} from 'firebase/firestore';
import { Trans, useTranslation } from 'react-i18next';
import { segmentPayload } from './segment-payload';
import { estimateLine } from './estimate-line';
import {
  CONTACT_LIST_CAP,
  CONTACT_SEARCH_LIMIT,
  buildContactList,
  contactIdsOf,
  contactKey,
  crmOption,
  matchesContact,
  personOption,
  prefixVariants,
  selectedContacts,
  type ContactOption,
} from './audience-contacts';
import { db } from '../../../lib/firebase';
import type { PersonType, InstitutionalRole } from '@dosfilos/domain';
import type { CampaignDraft, AudienceSegment, AudienceSegmentType } from './types';
import { callable } from '../../../lib/callable';

// ─── Constants ────────────────────────────────────────────────────────────────

// EL RÓTULO SALE POR CLAVE, no escrito acá: lo lee el usuario, y una constante
// de módulo no puede llamar a `t`. La pantalla lo resuelve al dibujar.
const AUDIENCE_OPTIONS: { value: AudienceSegmentType; icon: React.ReactNode }[] = [
  { value: 'all_crm', icon: <PublicRounded fontSize="small" /> },
  { value: 'tag', icon: <LocalOfferRounded fontSize="small" /> },
  { value: 'by_person_type', icon: <PersonRounded fontSize="small" /> },
  { value: 'by_institutional_role', icon: <SchoolRounded fontSize="small" /> },
  { value: 'by_program', icon: <MenuBookRounded fontSize="small" /> },
  { value: 'by_course', icon: <LibraryBooksRounded fontSize="small" /> },
  { value: 'active_term_students', icon: <EventAvailableRounded fontSize="small" /> },
  { value: 'custom_contacts', icon: <TuneRounded fontSize="small" /> },
];

const PERSON_TYPE_OPTIONS: { value: PersonType }[] = [
  { value: 'prospecto-académico' },
  { value: 'student' },
  { value: 'alumni' },
  { value: 'externo' },
  { value: 'contact' },
  { value: 'docente' },
  { value: 'staff' },
];

const INSTITUTIONAL_ROLE_OPTIONS: { value: InstitutionalRole }[] = [
  { value: 'docente' },
  { value: 'tutor' },
  { value: 'decano' },
  { value: 'academico' },
  { value: 'coordinador' },
  { value: 'capellan' },
  { value: 'staff' },
];

interface ProgramOption {
  id: string;
  name: string;
}
interface CourseOption {
  id: string;
  name: string;
  programId: string;
}
interface TermOption {
  id: string;
  name: string;
  status?: string;
}
interface SectionLite {
  id: string;
  courseId: string;
  termId: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Step2AudienceProps {
  draft: CampaignDraft;
  tenantId: string;
  onChange: (patch: Partial<CampaignDraft>) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Step2Audience({ draft, tenantId, onChange }: Step2AudienceProps) {
  const { t } = useTranslation('sis');
  const segment = draft.audienceSegment;

  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  // Contactos específicos (ver audience-contacts.ts): la lista completa hasta
  // CONTACT_LIST_CAP por fuente; con más, se busca.
  const [contactMode, setContactMode] = useState<'loading' | 'list' | 'search' | 'unavailable'>(
    'loading',
  );
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [resolved, setResolved] = useState<Map<string, ContactOption>>(new Map());
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<ContactOption[]>([]);
  const [terms, setTerms] = useState<TermOption[]>([]);
  const [sections, setSections] = useState<SectionLite[]>([]);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);

  // Load programs
  useEffect(() => {
    if (!tenantId) return;
    getDocs(
      query(collection(db, 'tenants', tenantId, 'programs'), orderBy('name'), limit(500)),
    ).then((snap) =>
      setPrograms(
        snap.docs
          .filter((d) => !d.data().deletedAt)
          .map((d) => ({ id: d.id, name: d.data().name })),
      ),
    );
  }, [tenantId]);

  // Load courses
  useEffect(() => {
    if (!tenantId) return;
    getDocs(
      query(collection(db, 'tenants', tenantId, 'courses'), orderBy('name'), limit(500)),
    ).then((snap) =>
      setCourses(
        snap.docs
          .filter((d) => !d.data().deletedAt)
          .map((d) => ({ id: d.id, name: d.data().name, programId: d.data().programId ?? '' })),
      ),
    );
  }, [tenantId]);

  // Load contacts for custom_contacts (SIS people + CRM contacts)
  useEffect(() => {
    if (!tenantId || segment.type !== 'custom_contacts') return;
    let cancelled = false;
    setContactMode('loading');
    Promise.all([
      getDocs(
        query(
          collection(db, 'tenants', tenantId, 'people'),
          orderBy('firstName'),
          limit(CONTACT_LIST_CAP + 1),
        ),
      ),
      getDocs(
        query(
          collection(db, 'tenants', tenantId, 'crmContacts'),
          orderBy('name'),
          limit(CONTACT_LIST_CAP + 1),
        ),
      ),
    ])
      .then(([peopleSnap, crmSnap]) => {
        if (cancelled) return;
        if (peopleSnap.size > CONTACT_LIST_CAP || crmSnap.size > CONTACT_LIST_CAP) {
          setContacts([]);
          setContactMode('search');
          return;
        }
        const docs = (snap: typeof peopleSnap) =>
          snap.docs.map((d) => ({ id: d.id, data: d.data() }));
        setContacts(buildContactList(docs(peopleSnap), docs(crmSnap)));
        setContactMode('list');
      })
      .catch(() => {
        if (!cancelled) setContactMode('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, segment.type]);

  // Los ELEGIDOS se leen por id, estén o no en la lista: así se ven y un
  // cambio en el campo no los borra.
  const contactIdsKey = (segment.contactIds ?? []).join('|');
  useEffect(() => {
    if (!tenantId || segment.type !== 'custom_contacts') return;
    const listed = new Set(contacts.map(contactKey));
    const missing = (segment.contactIds ?? []).filter((k) => !listed.has(k) && !resolved.has(k));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const found = new Map<string, ContactOption>();
      for (const [prefix, col, toOption] of [
        ['sis:', 'people', personOption],
        ['crm:', 'crmContacts', crmOption],
      ] as const) {
        const ids = missing.filter((k) => k.startsWith(prefix)).map((k) => k.slice(prefix.length));
        for (let i = 0; i < ids.length; i += 30) {
          const snap = await getDocs(
            query(
              collection(db, 'tenants', tenantId, col),
              where(documentId(), 'in', ids.slice(i, i + 30)),
            ),
          );
          for (const d of snap.docs) {
            const o = toOption({ id: d.id, data: d.data() });
            if (o) found.set(contactKey(o), o);
          }
        }
      }
      if (!cancelled && found.size) setResolved((prev) => new Map([...prev, ...found]));
    })().catch(() => {
      /* quedan como marcador: se conservan igual */
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId, segment.type, contactIdsKey, contacts]);

  // Con más de CONTACT_LIST_CAP: búsqueda por prefijo (nombre o correo).
  useEffect(() => {
    if (contactMode !== 'search' || contactSearch.trim().length < 2) {
      // Sin crear un arreglo nuevo si ya está vacío: no hace falta re-renderizar.
      setContactResults((prev) => (prev.length ? [] : prev));
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      const token = [...contactSearch.trim().split(/\s+/)].sort((a, b) => b.length - a.length)[0]!;
      const range = (col: string, field: string, prefix: string) =>
        getDocs(
          query(
            collection(db, 'tenants', tenantId, col),
            orderBy(field),
            startAt(prefix),
            endAt(`${prefix}\uf8ff`),
            limit(CONTACT_SEARCH_LIMIT),
          ),
        );
      const queries = [
        ...prefixVariants(token).flatMap((v) => [
          range('people', 'firstName', v).then((s) => ({ s, f: personOption })),
          range('people', 'lastName', v).then((s) => ({ s, f: personOption })),
          range('crmContacts', 'name', v).then((s) => ({ s, f: crmOption })),
        ]),
        range('people', 'email', token.toLowerCase()).then((s) => ({ s, f: personOption })),
        range('crmContacts', 'email', token.toLowerCase()).then((s) => ({ s, f: crmOption })),
      ];
      Promise.all(queries)
        .then((results) => {
          if (cancelled) return;
          const byKey = new Map<string, ContactOption>();
          for (const { s, f } of results) {
            for (const d of s.docs) {
              const o = f({ id: d.id, data: d.data() });
              if (o && matchesContact(o, contactSearch)) byKey.set(contactKey(o), o);
            }
          }
          setContactResults([...byKey.values()].sort((a, b) => a.name.localeCompare(b.name)));
        })
        .catch(() => {
          if (!cancelled) setContactResults([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [contactMode, contactSearch, tenantId]);

  // Los conocidos: los leídos por id, los elegidos desde la búsqueda y la
  // lista. SIN los resultados de búsqueda: si el VALOR del campo cambiara de
  // referencia al escribir, MUI borraría el texto que se está escribiendo.
  const knownContacts = useMemo(() => {
    const m = new Map(resolved);
    for (const c of contacts) m.set(contactKey(c), c);
    return m;
  }, [resolved, contacts]);
  const selectedContactValue = useMemo(
    () => selectedContacts(segment.contactIds ?? [], knownContacts),
    [segment.contactIds, knownContacts],
  );
  const contactOptions = contactMode === 'search' ? contactResults : contacts;

  // «Por materia»: los períodos, TODOS (antes `limit(50)`: con 129 períodos
  // importados quedaban fuera los más viejos), y las secciones SOLO del período
  // elegido (antes se leían todas, con `limit(2000)`, para cruzar curso y
  // período). Los cursos no tienen período: el vínculo vive en las secciones.
  useEffect(() => {
    if (!tenantId || segment.type !== 'by_course' || terms.length > 0) return;
    getDocs(query(collection(db, 'tenants', tenantId, 'terms'), orderBy('startDate', 'desc'))).then(
      (termSnap) =>
        setTerms(
          termSnap.docs.map((d) => {
            const status = d.data().status as string | undefined;
            return {
              id: d.id,
              name: (d.data().name as string) ?? d.id,
              ...(status ? { status } : {}),
            };
          }),
        ),
    );
  }, [tenantId, segment.type, terms.length]);

  useEffect(() => {
    if (!tenantId || segment.type !== 'by_course' || !segment.termId) return;
    let cancelled = false;
    getDocs(
      query(collection(db, 'tenants', tenantId, 'sections'), where('termId', '==', segment.termId)),
    ).then((secSnap) => {
      if (cancelled) return;
      setSections(
        secSnap.docs
          .filter((d) => d.data().status !== 'cancelled')
          .map((d) => ({
            id: d.id,
            courseId: (d.data().courseId as string) ?? '',
            termId: (d.data().termId as string) ?? '',
          })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId, segment.type, segment.termId]);

  // Courses that have a section in the selected ciclo.
  const coursesInTerm = useMemo(() => {
    if (!segment.termId) return [];
    const courseIds = new Set(
      sections.filter((s) => s.termId === segment.termId).map((s) => s.courseId),
    );
    return courses.filter((c) => courseIds.has(c.id));
  }, [sections, courses, segment.termId]);

  // Default the ciclo to the active term (in_progress → open → newest) once ready.
  useEffect(() => {
    if (segment.type !== 'by_course' || segment.termId || terms.length === 0) return;
    const active =
      terms.find((tm) => tm.status === 'in_progress') ??
      terms.find((tm) => tm.status === 'open') ??
      terms[0];
    if (active) setSegment({ termId: active.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment.type, segment.termId, terms]);

  function setSegment(update: Partial<AudienceSegment>) {
    // Cambiar la audiencia invalida la estimación anterior (y sus excluidos).
    const rest = { ...draft };
    delete rest.estimatedCount;
    delete rest.estimatedExcludedNoEmail;
    delete rest.estimatedExcludedDeceased;
    onChange({
      ...rest,
      audienceSegment: { ...segment, ...update },
    });
  }

  /** Cuántas personas recibirían esto — LO CUENTA EL SERVIDOR.
   *
   *  ANTES SE RECALCULABA ACÁ, reimplementando la segmentación contra
   *  Firestore. Tres problemas, y el tercero es el que importa:
   *  · un `limit(500)` que truncaba sin decirlo, así que el «~N» podía ser
   *    cualquier cosa en una base grande;
   *  · para «etiqueta» y «cargo institucional» caía a un caso genérico que
   *    contaba TODAS las personas — un número sin ninguna relación con lo que
   *    se iba a enviar;
   *  · eran DOS implementaciones del mismo criterio, y la que se veía no era la
   *    que mandaba. Dos implementaciones sólo pueden divergir.
   *
   *  Ahora el número sale del mismo `AudienceResolver` que hará el envío, así
   *  que la cifra de la pantalla y la audiencia real no pueden discrepar. */
  async function estimateCount() {
    setEstimating(true);
    setEstimateError(null);
    try {
      const countFn = callable<
        { tenantId: string; audienceSegment: object },
        { count: number; excludedNoEmail?: number; excludedDeceased?: number }
      >('countCampaignAudience');
      const { data } = await countFn({ tenantId, audienceSegment: segmentPayload(segment) });
      onChange({
        estimatedCount: data.count,
        estimatedExcludedNoEmail: data.excludedNoEmail ?? 0,
        estimatedExcludedDeceased: data.excludedDeceased ?? 0,
      });
    } catch {
      // Un fallo acá no se puede tragar: el número anterior seguiría en
      // pantalla, y la persona lo leería como vigente.
      onChange({
        estimatedCount: undefined,
        estimatedExcludedNoEmail: undefined,
        estimatedExcludedDeceased: undefined,
      });
      setEstimateError(t('campaign.estimateError'));
    } finally {
      setEstimating(false);
    }
  }

  return (
    <Stack spacing={3} maxWidth={900} mx="auto">
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
          <PeopleAltRounded color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            {t('campaign.step2.title')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('campaign.step2.subtitle')}
        </Typography>
      </Box>

      {/* Audience type */}
      <FormControl fullWidth>
        <InputLabel>{t('campaign.step2.type')}</InputLabel>
        <Select
          value={segment.type}
          label={t('campaign.step2.type')}
          onChange={(e) => setSegment({ type: e.target.value as AudienceSegmentType })}
          renderValue={(value) => {
            const opt = AUDIENCE_OPTIONS.find((o) => o.value === value);
            return opt ? (
              <Stack direction="row" spacing={1} alignItems="center">
                {opt.icon}
                <span>{t(`campaign.audience.${opt.value}`)}</span>
              </Stack>
            ) : (
              value
            );
          }}
        >
          {AUDIENCE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>{o.icon}</Box>
                <span>{t(`campaign.audience.${o.value}`)}</span>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* by tag */}
      {segment.type === 'tag' && (
        <TextField
          label={t('campaign.step2.tag')}
          value={segment.tag ?? ''}
          onChange={(e) => setSegment({ tag: e.target.value })}
          placeholder={t('campaign.step2.tagPlaceholder')}
          helperText={t('campaign.step2.tagHelp')}
          fullWidth
          size="small"
        />
      )}

      {/* by person type */}
      {segment.type === 'by_person_type' && (
        <Box>
          <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
            {t('campaign.step2.pickPersonTypes')}
          </Typography>
          <FormGroup row sx={{ gap: 0.5 }}>
            {PERSON_TYPE_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    size="small"
                    checked={(segment.personTypes ?? []).includes(opt.value)}
                    onChange={(e) => {
                      const current = segment.personTypes ?? [];
                      const updated = e.target.checked
                        ? [...current, opt.value]
                        : current.filter((v) => v !== opt.value);
                      setSegment({ personTypes: updated });
                    }}
                  />
                }
                label={
                  <Typography variant="body2">{t(`campaign.personType.${opt.value}`)}</Typography>
                }
                sx={{ mr: 1 }}
              />
            ))}
          </FormGroup>
        </Box>
      )}

      {/* by institutional role */}
      {segment.type === 'by_institutional_role' && (
        <Box>
          <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
            {t('campaign.step2.pickRoles')}
          </Typography>
          <FormGroup row sx={{ gap: 0.5 }}>
            {INSTITUTIONAL_ROLE_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    size="small"
                    checked={(segment.roles ?? []).includes(opt.value)}
                    onChange={(e) => {
                      const current = segment.roles ?? [];
                      const updated = e.target.checked
                        ? [...current, opt.value]
                        : current.filter((v) => v !== opt.value);
                      setSegment({ roles: updated });
                    }}
                  />
                }
                label={<Typography variant="body2">{t(`campaign.role.${opt.value}`)}</Typography>}
                sx={{ mr: 1 }}
              />
            ))}
          </FormGroup>
        </Box>
      )}

      {/* by program */}
      {segment.type === 'by_program' && (
        <FormControl fullWidth size="small">
          <InputLabel>{t('campaign.step2.program')}</InputLabel>
          <Select
            value={segment.programId ?? ''}
            label={t('campaign.step2.program')}
            onChange={(e) => setSegment({ programId: e.target.value })}
          >
            {programs.length === 0 && (
              <MenuItem disabled>{t('campaign.step2.loadingPrograms')}</MenuItem>
            )}
            {programs.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            {t('campaign.step2.programHelp')}
          </Typography>
        </FormControl>
      )}

      {/* by course — cascading Ciclo → Materia */}
      {segment.type === 'by_course' && (
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('campaign.byCourse.cycle')}</InputLabel>
            <Select
              value={terms.some((tm) => tm.id === segment.termId) ? (segment.termId ?? '') : ''}
              label={t('campaign.byCourse.cycle')}
              onChange={(e) => setSegment({ termId: e.target.value, courseId: '' })}
            >
              {terms.length === 0 && (
                <MenuItem disabled>{t('campaign.byCourse.loadingCycles')}</MenuItem>
              )}
              {terms.map((tm) => (
                <MenuItem key={tm.id} value={tm.id}>
                  {tm.name}
                  {tm.status === 'in_progress' ? ` · ${t('campaign.byCourse.activeTag')}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={!segment.termId}>
            <InputLabel>{t('campaign.byCourse.subject')}</InputLabel>
            <Select
              value={
                coursesInTerm.some((c) => c.id === segment.courseId) ? (segment.courseId ?? '') : ''
              }
              label={t('campaign.byCourse.subject')}
              onChange={(e) => setSegment({ courseId: e.target.value })}
            >
              {segment.termId && coursesInTerm.length === 0 && (
                <MenuItem disabled>{t('campaign.byCourse.noSubjects')}</MenuItem>
              )}
              {coursesInTerm.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" mt={0.5}>
              {t('campaign.byCourse.helper')}
            </Typography>
          </FormControl>
        </Stack>
      )}

      {/* custom contacts */}
      {segment.type === 'custom_contacts' && (
        <Autocomplete
          multiple
          options={contactOptions}
          loading={contactMode === 'loading'}
          groupBy={(o) =>
            o.source === 'SIS' ? t('campaign.step2.groupSis') : t('campaign.step2.groupCrm')
          }
          getOptionLabel={(o) =>
            o.unresolved ? t('campaign.step2.unresolvedContact') : `${o.name} <${o.email}>`
          }
          isOptionEqualToValue={(a, b) => contactKey(a) === contactKey(b)}
          // El valor son TODOS los elegidos (los no cargados, como marcador):
          // un cambio en el campo nunca quita lo que la pantalla no cargó.
          value={selectedContactValue}
          onChange={(_, selected) => {
            // Lo elegido desde una búsqueda queda conocido (se ve su nombre).
            const picked = selected.filter((c) => !c.unresolved);
            if (picked.some((c) => !knownContacts.has(contactKey(c)))) {
              setResolved((prev) => {
                const next = new Map(prev);
                for (const c of picked) next.set(contactKey(c), c);
                return next;
              });
            }
            setSegment({ contactIds: contactIdsOf(selected) });
          }}
          {...(contactMode === 'search'
            ? {
                filterOptions: (x: ContactOption[]) => x,
                onInputChange: (_: unknown, value: string, reason: string) => {
                  if (reason === 'input') setContactSearch(value);
                },
                noOptionsText:
                  contactSearch.trim().length < 2
                    ? t('campaign.step2.typeToSearch')
                    : t('campaign.step2.noContactMatches'),
              }
            : contactMode === 'unavailable'
              ? { noOptionsText: t('campaign.step2.contactsUnavailable') }
              : {})}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={
                  option.unresolved
                    ? t('campaign.step2.unresolvedContact')
                    : option.name || option.email
                }
                size="small"
                {...getTagProps({ index })}
                key={contactKey(option)}
              />
            ))
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          renderInput={(params) => (
            <TextField
              {...(params as any)}
              label={t('campaign.step2.pickContacts')}
              placeholder={t('campaign.step2.searchContacts')}
              helperText={t('campaign.step2.contactsSelected', {
                count: (segment.contactIds ?? []).length,
              })}
            />
          )}
        />
      )}

      {/* active term students */}
      {segment.type === 'active_term_students' && (
        <Box>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            {t('campaign.step2.activeTermHelp')}
          </Typography>
          <FormGroup sx={{ gap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={segment.missingEnrollment ?? true}
                  onChange={(e) => setSegment({ missingEnrollment: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t('campaign.step2.noCourses')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('campaign.step2.noCoursesHelp')}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={segment.missingPaymentPlan ?? false}
                  onChange={(e) => setSegment({ missingPaymentPlan: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t('campaign.step2.noPlan')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('campaign.step2.noPlanHelp')}
                  </Typography>
                </Box>
              }
            />
          </FormGroup>
          {!segment.missingEnrollment && !segment.missingPaymentPlan && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {t('campaign.step2.pickAtLeastOne')}
            </Alert>
          )}
        </Box>
      )}

      <Divider />

      {/* Estimate section */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            estimating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <GroupsRounded fontSize="small" />
            )
          }
          onClick={estimateCount}
          disabled={estimating}
          sx={{ borderRadius: 2 }}
        >
          {estimating ? t('campaign.estimate.estimating') : t('campaign.estimate.button')}
        </Button>

        {estimateError && (
          <Alert severity="error" sx={{ py: 0 }}>
            {estimateError}
          </Alert>
        )}

        {draft.estimatedCount !== undefined && (
          <Paper
            variant="outlined"
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              borderColor: 'success.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <GroupsRounded fontSize="small" color="success" />
            <Typography variant="body2" fontWeight={600}>
              {estimateLine(draft, t)}
            </Typography>
          </Paper>
        )}
      </Stack>

      {segment.type === 'all_crm' && (
        <Alert severity="info" icon={<PublicRounded fontSize="small" />}>
          {/* LA NEGRITA VA DENTRO DE LA TRADUCCIÓN, no partiendo la frase en
              dos claves: en otro idioma el énfasis puede caer en otra palabra o
              en otro sitio, y dos mitades cosidas obligan a un orden que no
              todos los idiomas admiten. */}
          <Trans i18nKey="campaign.step2.allHelp" ns="sis" components={{ b: <strong /> }} />
        </Alert>
      )}
    </Stack>
  );
}
